import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  User,
  Calendar,
  Phone,
  CreditCard,
  Building2,
  HeartHandshake,
  Users,
  MapPin,
  Home,
  Landmark,
  BadgeCheck,
  PhoneCall,
  Smartphone,
  BarChart3,
  Banknote,
  Wallet,
  AlertTriangle,
  Info,
  List,
  Loader2,
  Pencil,
  ChevronDown,
  Download,
  ShieldAlert,
  FileText,
  HandCoins,
  NotebookPen,
  CircleDollarSign,
  Receipt,
  BadgePercent,
  Trash2,
  ArrowLeftRight,
  ChevronRight,
  Pen,
} from "lucide-react";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { apiFetch } from "@/lib/api";
import { helper } from "@/lib/helper";
import { cn } from "@/lib/utils";
import ActiveLoanPayments from "./tabs/ActiveLoanPaymentsTab";
import CustomerNotesTab from "./tabs/CustomerNotesTab";
import Promise2PayTab from "./tabs/Promise2PayTab";
import ActiveLoanChargesTab from "./tabs/ActiveLoanChargesTab";
import CustomerLoans from "./tabs/CustomerLoans";
import ActiveLoanScheduleTab from "./tabs/ActiveLoanScheduleTab";
import CustomerAssetsTab from "./tabs/CustomerAssetsTab";
import CustomerRefereesTab from "./tabs/CustomerRefereesTab";
import CustomerOtherDetailsTab from "./tabs/CustomerOtherDetailsTab";
import CustomerBusinessTab from "./tabs/CustomerBusinessTab";
import CustomerLocationTab from "./tabs/CustomerLocationTab";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { toastError } from "@/lib/toast";
import CustomerAttachmentsTab from "./tabs/CustomerAttachmentsTab";
import { ActionDialog } from "@/components/ActionDialog";
import { hasPerm } from "@/lib/auth";



/** Types */
type ActiveLoan = {
  id?: number | string;
  loan_amount?: number | string;
  total_due?: number | string;
  paid?: number | string;
  balance?: number | string;
  due_today?: number | string;
  penalty_charged?: number | string;
  due_date?: string;
  loan_days?: number;
  duplum_locked?: number;
};



type CustomerProfile = {
  id: number | string;
  photo_url?: string;

  regDate?: string;
  fname?: string;
  mname?: string;
  sname?: string;
  aka?: string;
  national_id?: string;
  phone_number?: string;
  branch_name?: string;
  marital_status?: string;
  dependents_number?: number | string;
  gender_name?: string;
  dob?: string;
  age_text?: string;
  created_at?: string;
  CREATEDBY?: string;
  USERNAME?: string;
  USERNAME2?: string;
  status_text?: string;

  account_status?: number;
  account?: string | number;
  town?: string;
  landmark?: string;
  address?: string;
  alt_phone?: string;
  mpesa_no1?: string;
  mpesa_no2?: string;
  scores?: number | string;
  loans_issued?: number | string;
  credit_limit?: number | string;
  overpayment?: number | string;

  defaulted_times?: number;
  image_urls?: any

};

type TabKey =
  | "loan_status"
  | "schedule"
  | "attachments"
  | "assets"
  | "promise_to_pay"
  | "referee"
  | "business"
  | "other_details"
  | "location";

export default function CustomerProfilePage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const nonce = (location.state as any)?.nonce;

  const dir = (document.documentElement.getAttribute("dir") as "ltr" | "rtl") || "ltr";
  const isRTL = dir === "rtl";
  const pinRight = isRTL ? "text-left" : "text-right";

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [c, setC] = useState<CustomerProfile | null>(null);

  /** active loan lazy */
  const [activeLoanLoading, setActiveLoanLoading] = useState(false);
  const [activeLoanErr, setActiveLoanErr] = useState("");
  const [activeLoan, setActiveLoan] = useState<ActiveLoan | null>(null);
  const [dueTodayBal, setDueTodayBal] = useState<number | string>("-");
  const [defaultedInstallment, setDefaultedInstallment] = useState<number | string>("-");






  /** right-side tabs (big sections) */
  const [activeTab, setActiveTab] = useState<TabKey>("loan_status");
  const [tabLoaded, setTabLoaded] = useState<Record<TabKey, boolean>>({
    loan_status: true,
    schedule: false,
    attachments: false,
    assets: false,
    promise_to_pay: false,
    referee: false,
    business: false,
    other_details: false,
    location: false,
  });

  const perPage = 10;

  const selectedCustomerId = useMemo(() => {
    const stateId = (location.state as any)?.selectedCustomer;
    const stored = sessionStorage.getItem("selectedCustomer");
    return String(stateId || stored || id || "0");
  }, [location.state, id]);

  const isLead = Number(c?.account_status) === 6;

  /** Profile load (FAST) */
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setLoading(true);
        setErr("");

        sessionStorage.setItem("selectedCustomer", selectedCustomerId);

        const res = await apiFetch<any>(`/customer-profile/${selectedCustomerId}`);
        if (!alive) return;

        const data = res?.data?.data ?? res?.data ?? res;

        setC({ ...(data as CustomerProfile) });

        // reset lists when customer changes




        // reset active loan lazy
        setActiveLoan(null);
        setDueTodayBal("-");
        setDefaultedInstallment("-");
        setActiveLoanErr("");

        // reset tabs loaded
        setActiveTab("loan_status");
        setTabLoaded((prev) => ({
          ...prev,
          loan_status: true,
          schedule: false,
          attachments: false,
          assets: false,
          promise_to_pay: false,
          referee: false,
          business: false,
          other_details: false,
          location: false,
        }));
      } catch (e: any) {
        if (!alive) return;
        setErr(e?.message || "Failed to load customer profile");
        setC(null);
      } finally {
        if (alive) setLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [nonce, selectedCustomerId]);

  /** Computed */
  const fullName = useMemo(() => {
    if (!c) return "-";
    return [c.fname, c.mname, c.sname].filter(Boolean).join(" ").trim() || "-";
  }, [c]);

  const bdoName = useMemo(() => {
    if (!c) return "-";
    return [c.USERNAME, c.USERNAME2].filter(Boolean).join(" ").trim() || "-";
  }, [c]);

  type LoanProduct = { id: number; name: string };

  const [loanProducts, setLoanProducts] = useState<LoanProduct[]>([]);
  const [loanProductsLoading, setLoanProductsLoading] = useState(false);


  async function ensureLoanProductsLoaded() {
    if (loanProducts.length) return; // already loaded
    setLoanProductsLoading(true);
    try {
      const res = await apiFetch<any>(`/customer-profile/${id}/actions/loan-products`);
      setLoanProducts(res?.data ?? []);
    } finally {
      setLoanProductsLoading(false);
    }
  }

  /** Actions (wire later) */
  type ActionKey =
    | "change-phone"
    | "change-status"
    | "add-alt-phone"
    | "add-mpesa-alt"
    | "customer-notes"
    | "increase-credit-limit"
    | "apply-loan"
    | "promise-to-pay";


  type FieldType = "text" | "number" | "date" | "textarea" | "select";

  type FieldDef = {
    name: string;
    label: string;
    type: FieldType;
    placeholder?: string;
    required?: boolean;
    readonly?: boolean;
    colSpan?: 1 | 2;
    options?: Array<{ value: string; label: string }>;
  };

  type ActionConfig = {
    title: string;
    submitLabel: string;
    endpoint: (customerId: string) => string;
    defaultValues: (customer: any) => Record<string, any>;
    fields: FieldDef[];
  };



  const STATUS_OPTIONS = [
    { value: "0", label: "Pending" },
    { value: "1", label: "Active" },
    { value: "2", label: "Rejected" },
    { value: "3", label: "Blacklisted" },
    { value: "4", label: "Suspend" },
    { value: "5", label: "Dormant" },
    { value: "6", label: "Lead" },
    { value: "7", label: "Incomplete Profile" },
    { value: "8", label: "Reposes" },
    { value: "9", label: "BLC (Bad Luck Customer)" },
    { value: "10", label: "BFC (Bad Faith Customer)" },
    { value: "11", label: "Deceased" },
  ];

  const NOTE_STATUS_OPTIONS = [
    { value: "0", label: "Pending" },
    { value: "1", label: "Escalated" },
    { value: "2", label: "Resolved" },
  ];

  const actionsConfig: Record<ActionKey, ActionConfig> = {
    "change-phone": {
      title: "Change Primary Number",
      submitLabel: "Change",
      endpoint: (id) => `/customer-profile/${id}/actions/change-primary-number`,
      defaultValues: (c) => ({
        fname: c?.fname ?? "",
        phone_number: "",
      }),
      fields: [
        { name: "fname", label: "Fname", type: "text", required: true, colSpan: 2, readonly: true },
        { name: "phone_number", label: "New Phone Number", type: "text", required: true, colSpan: 2 },
      ],
    },

    "change-status": {
      title: "Change Customer Status",
      submitLabel: "Process",
      endpoint: (id) => `/customer-profile/${id}/actions/status`,
      defaultValues: (c) => ({
        status: String(c?.account_status ?? "0"),

        more_info: "",
      }),
      fields: [
        {
          name: "status",
          label: "Status",
          type: "select",
          required: true,
          options: STATUS_OPTIONS,
          colSpan: 2,
        },
        {
          name: "more_info",
          label: "Comment",
          type: "textarea",
          colSpan: 2,
        },
      ],
    },

    "add-alt-phone": {
      title: "Add Alternative Phone Number",
      submitLabel: "Create",
      endpoint: (id) => `/customer-profile/${id}/actions/alt-phone`,
      defaultValues: () => ({ phone_number: "" }),
      fields: [{ name: "phone_number", label: "Phone Number", type: "text", required: true, colSpan: 2 }],
    },

    "increase-credit-limit": {
      title: "Add Alternative Phone Number",
      submitLabel: "Create",
      endpoint: (id) => `/customer-profile/${id}/actions/limit`,
      defaultValues: () => ({ credit_limit: String(c?.credit_limit ?? "0"), }),
      fields: [
        { name: "credit_limit", label: "Credit Limit", type: "text", colSpan: 2, readonly: true },
        { name: "newCreditLimit", label: "New Credit Limit", type: "text", colSpan: 2, required: true, },
      ],
    },

    "add-mpesa-alt": {
      title: "MPesa Alternative Number",
      submitLabel: "Create",
      endpoint: (id) => `/customer-profile/${id}/actions/mpesa-alt`,
      defaultValues: () => ({ mpesa_no1: "", mpesa_no2: "" }),
      fields: [
        { name: "mpesa_no1", label: "Mpesa Number 1", type: "text", required: true, colSpan: 2 },
        { name: "mpesa_no2", label: "Mpesa Number 2", type: "text", colSpan: 2 },
      ],
    },

    "customer-notes": {
      title: "Customer Notes",
      submitLabel: "Process",
      endpoint: (id) => `/customer-profile/${id}/actions/create-note`,
      defaultValues: () => ({ note: "", note_status: 0 }),
      fields: [
        { name: "note", label: "Note", type: "textarea", required: true, colSpan: 2 },
        { name: "note_status", label: "Status", type: "select", required: true, options: NOTE_STATUS_OPTIONS, colSpan: 2 },
      ],
    },

    "promise-to-pay": {
      title: "Promise to Pay",
      submitLabel: "Create",
      endpoint: (id) => `/customer-profile/${id}/actions/promise-to-pay`,
      defaultValues: () => ({
        note: "",
        amount: "",
        date: new Date().toISOString().slice(0, 10),
      }),
      fields: [
        { name: "note", label: "Note", type: "textarea", required: true, colSpan: 2 },
        { name: "amount", label: "Amount", type: "number", required: true, colSpan: 2 },
        { name: "date", label: "Date", type: "date", required: true, colSpan: 2 },
      ],
    },
    "apply-loan": { //apply loan
      title: "Customer Loan Application",
      submitLabel: "Create",
      endpoint: (id) => `/customer-profile/${id}/actions/apply-loan`,
      defaultValues: () => ({
        product_id: "",
        amount: "",
        date: new Date().toISOString().slice(0, 10),
      }),
      fields: [
        {
          name: "product_id",
          label: loanProductsLoading ? "Loan Product (loading...)" : "Loan Product",
          type: "select",
          required: true,
          colSpan: 2,
          options: loanProducts.map((p) => ({ value: String(p.id), label: p.name })),
        },
        { name: "amount", label: "Amount", type: "number", required: true, colSpan: 2 },
        { name: "date", label: "Date", type: "date", required: true, colSpan: 2 },
      ],
    },
  };

  const [activeAction, setActiveAction] = useState<ActionKey | null>(null);

  const openAction = async (k: ActionKey) => {
    if (k === "apply-loan") {
      await ensureLoanProductsLoaded();
    }
    setActiveAction(k);
  };
  const closeAction = () => setActiveAction(null);

  const action = activeAction ? actionsConfig[activeAction] : null;



  async function fetchActiveLoan(customerId: string | number) {
    setActiveLoanLoading(true);
    setActiveLoanErr("");
    try {
      const res = await apiFetch<any>(`/customer-profile/${customerId}/active-loan`);
      const data = res?.data?.data ?? res?.data ?? res;

      setActiveLoan((data?.active_loan ?? null) as ActiveLoan | null);
      setDueTodayBal(data?.due_today_bal ?? "-");
      setDefaultedInstallment(data?.defaulted_installment ?? "-");
    } catch (e: any) {
      setActiveLoanErr(e?.message || "Failed to load active loan");
      setActiveLoan(null);
      setDueTodayBal("-");
      setDefaultedInstallment("-");
    } finally {
      setActiveLoanLoading(false);
    }
  }

  /** Lazy-load: active loan after profile ready (not for leads) */
  useEffect(() => {
    if (!loading && c?.id && !isLead) {
      fetchActiveLoan(c.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, c?.id, isLead]);





  /** Tabs: when user clicks a big section tab, we lazy mark loaded and (later) fetch that tab data */
  function openTab(tab: TabKey) {
    setActiveTab(tab);
    setTabLoaded((prev) => ({ ...prev, [tab]: true }));
    // here is where you will call fetch for that tab endpoint later (schedule, attachments, etc)
  }

  const StatusBadge = ({ text }: { text: string }) => (
    <Badge className="gradient-primary text-primary-foreground px-3 py-1">
      <BadgeCheck className="h-4 w-4 me-2" />
      {text}
    </Badge>
  );


  // inside component:
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);

  const navigate = useNavigate();

  const openConvertConfirm = () => {
    if (!c?.id) return;
    setConfirmOpen(true);
  };

  const convertLead = async () => {
    if (!c?.id) return;

    try {
      setConvertLoading(true);


      // ✅ open wizard WITHOUT id in URL (pass new customer id through state)
      navigate("/actions/new-customer", {
        state: { customerId: String(c.id), from: "convert", nonce: Date.now() },
      });

      setConfirmOpen(false);
    } catch (e: any) {
      toastError(e?.message || "Failed to convert lead");
    } finally {
      setConvertLoading(false);
    }
  };

  const photoUrl = c?.image_urls?.photo;
  //permissions
  const canConvertLead = hasPerm("customers-add"); //convert lead or create customer
  const canEditCustomer = hasPerm("edit-customer"); //edit customer
  const canIncreaseCreditLimit = hasPerm("loan-credit-limit");
  const canAddPromiseToPay = hasPerm("promise-topay");
  const canAddCustomerNote = hasPerm("customer-notes");
  const canIssueLoanCreditDebit = hasPerm("loan-debit-credit");
  const canCanChangeStatus = hasPerm("review-status");
  const canApplyLoanForCustomer = hasPerm("loan-application");
  const canEditCustomerPrimaryNumber = hasPerm("edit-phonenumber");
  const canEditLoan = hasPerm("edit-loan");
  const canAddAltPhoneNumber = hasPerm("alt-phonenumber"); //add alt or mpesa number
    // inside component:
   
    const [confirmLoading, setConfirmLoading] = useState(false);
     const convertLeadConfirmed = async () => {
      if (!selectedCustomerId) return;
  
      try {
        setConfirmLoading(true);
        // ✅ open wizard WITHOUT id in URL (pass via state)
        navigate("/actions/new-customer", { state: { customerId: String(selectedCustomerId), from: "convert" } });
  
        setConfirmOpen(false);
        selectedCustomerId;
        // reload leads list
        // load();
      } catch (e: any) {
        toastError(e?.message ?? "Failed to edit customer");
      } finally {
        setConfirmLoading(false);
      }
    };


  return (
    <div className="space-y-5" dir={dir}>
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold truncate">Customer Profile</h1>
          {c?.id ? (
            <div className="text-xs text-muted-foreground">
              Account: <span className="text-foreground font-medium">{helper.safe(c.account ?? c.id)}</span>
            </div>
          ) : null}
        </div>

        {c?.status_text ? <StatusBadge text={c.status_text} /> : null}
      </div>

      {loading ? (
        <Card className="shadow-card">
          <CardContent className="p-6 flex items-center gap-3 text-sm text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading customer profile...
          </CardContent>
        </Card>
      ) : null}

      {err ? (
        <Card className="shadow-card">
          <CardContent className="p-4 text-sm text-destructive">{err}</CardContent>
        </Card>
      ) : null}

      {!loading && c ? (
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
          {/* Top 4 panels */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-12 gap-4">
            {/* 1) Photo */}
            <Card className="shadow-card xl:col-span-3">
              <CardContent className="p-3">
                <div className="aspect-[3/4] w-full overflow-hidden rounded-xl bg-muted flex items-center justify-center">
                  {photoUrl ? (
                    <img src={photoUrl} alt={fullName} className="h-full w-full object-cover" />
                  ) : (
                    <User className="h-14 w-14 text-muted-foreground" />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* 2) Personal Information */}
            <Card className="shadow-card xl:col-span-3">
              <CardContent className="p-4">
                <SectionTitle>Personal Information</SectionTitle>

                <InfoRow icon={Calendar} label="Joined" value={helper.safe(c.regDate)} rightClass={pinRight} />
                <InfoRow icon={User} label="Name" value={fullName} rightClass={pinRight} />

                {!isLead && (
                  <>
                    <InfoRow icon={User} label="AKA" value={helper.safe(c.aka)} rightClass={pinRight} />
                    <InfoRow icon={CreditCard} label="Id Number" value={helper.safe(c.national_id)} rightClass={pinRight} />
                  </>
                )}

                <InfoRow icon={Phone} label="Phone" value={helper.safe(c.phone_number)} rightClass={pinRight} />
                <InfoRow icon={Building2} label="Branch" value={helper.safe(c.branch_name)} rightClass={pinRight} />

                {!isLead && (
                  <>
                    <InfoRow icon={HeartHandshake} label="Marital Status" value={helper.safe(c.marital_status)} rightClass={pinRight} />
                    <InfoRow icon={Users} label="Dependents" value={helper.safe(c.dependents_number)} rightClass={pinRight} />
                    <InfoRow icon={Users} label="Gender" value={helper.safe(c.gender_name)} rightClass={pinRight} />
                    <InfoRow icon={Calendar} label="DOB" value={helper.safe(c.dob)} rightClass={pinRight} />
                  </>
                )}

                {c.age_text ? <InfoRow icon={Calendar} label="Age" value={c.age_text} rightClass={pinRight} /> : null}

                <div className="my-3 h-px bg-border" />

                <InfoRow icon={Calendar} label="Created At" value={helper.compactDateTime(helper.safe(c.created_at))} rightClass={pinRight} />
                <InfoRow icon={User} label="Created By" value={helper.safe(c.CREATEDBY)} rightClass={pinRight} />
                <InfoRow icon={User} label="BDO" value={bdoName} rightClass={pinRight} />

                <div className="mt-4 text-sm font-semibold flex items-center justify-between">
                  <span className="italic text-foreground/80">Status:</span>
                  <span className="italic text-foreground">{helper.safe(c.status_text, "—")}</span>
                </div>
              </CardContent>
            </Card>

            {/* 3) More Information */}
            <Card className="shadow-card xl:col-span-3">
              <CardContent className="p-4">
                <SectionTitle>More Information</SectionTitle>

                <InfoRow icon={Home} label="Account" value={helper.safe(c.account ?? c.id)} rightClass={pinRight} />
                <InfoRow icon={MapPin} label="Town" value={helper.safe(c.town)} rightClass={pinRight} />

                {!isLead && (
                  <>
                    <InfoRow icon={Landmark} label="Landmark" value={helper.safe(c.landmark)} rightClass={pinRight} />

                    <div className="py-2">
                      <div className="flex items-start gap-2">
                        <MapPin className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-muted-foreground font-medium">Address:</div>
                          <div className="text-sm font-medium leading-relaxed break-words">{helper.safe(c.address)}</div>
                        </div>
                      </div>
                      <div className="mt-2 h-px bg-border/60" />
                    </div>
                    {c?.alt_phone !== null &&
                      c?.alt_phone !== undefined &&
                      c?.alt_phone !== "" && (
                        <InfoRow icon={PhoneCall} label="Alt Phone" value={helper.safe(c.alt_phone)} rightClass={pinRight} />
                      )}
                    {c?.mpesa_no1 !== null &&
                      c?.mpesa_no1 !== undefined &&
                      c?.mpesa_no1 !== "" && (
                        <InfoRow icon={Smartphone} label="Mpesa Number 1" value={helper.safe(c.mpesa_no1)} rightClass={pinRight} />
                      )}

                    {c?.mpesa_no2 !== null &&
                      c?.mpesa_no2 !== undefined &&
                      c?.mpesa_no2 !== "" && (
                        <InfoRow icon={Smartphone} label="Mpesa Number 2" value={helper.safe(c.mpesa_no2)} rightClass={pinRight} />
                      )}
                    <div className="my-3 h-px bg-border" />
                    {c?.scores !== null &&
                      c?.scores !== undefined &&
                      c?.scores !== "" && (
                        <InfoRow icon={BarChart3} label="Score" value={helper.nf(c.scores)} rightClass={pinRight} />
                      )}
                    <InfoRow icon={List} label="Loan Issued" value={helper.nf(c.loans_issued)} rightClass={pinRight} />
                    <InfoRow icon={Banknote} label="Limit" value={helper.nf(c.credit_limit)} rightClass={pinRight} />
                    {c?.overpayment !== null &&
                      c?.overpayment !== undefined &&
                      c?.overpayment !== "" && (
                        <InfoRow icon={Wallet} label="OverPayment" value={helper.nf(c.overpayment)} rightClass={pinRight} />
                      )}
                  </>
                )}
              </CardContent>
            </Card>

            {/* 4) Loan Information (ACTIVE LOAN LAZY) */}
            <Card className="shadow-card xl:col-span-3">
              <CardContent className="p-4">
                {isLead ? (
                  <div className="space-y-3">
                    <SectionTitle>Lead Actions</SectionTitle>

                    {canConvertLead && (
                      <>
                        <Button
                          className="gap-2"
                          onClick={openConvertConfirm}
                          disabled={convertLoading}
                        >
                          <ArrowLeftRight className="h-4 w-4" />
                          Convert to Customer
                        </Button>
                      </>
                    )}
                    <div className="text-xs text-muted-foreground">TODO: call backend convert endpoint, then reload profile.</div>
                    <ConfirmDialog
                      open={confirmOpen}
                      onOpenChange={(v) => !convertLoading && setConfirmOpen(v)}
                      title="Convert lead to customer?"
                      description={
                        c
                          ? `Convert ${[c.fname, c.sname].filter(Boolean).join(" ") || "this lead"} to a customer and open the wizard?`
                          : "Convert this lead to a customer and open the wizard?"
                      }
                      confirmText="Convert & Open Wizard"
                      cancelText="Cancel"
                      loading={convertLoading}
                      onConfirm={convertLead}
                    />
                  </div>


                ) : (
                  <>
                    <SectionTitle>Loan Information</SectionTitle>

                    {activeLoanLoading ? (
                      <div className="text-sm text-muted-foreground flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading loan summary...
                      </div>
                    ) : activeLoanErr ? (
                      <div className="text-sm text-destructive">{activeLoanErr}</div>
                    ) : activeLoan ? (
                      <>
                        <LoanRow label="LoanId" value={activeLoan.id} />
                        <LoanRow label="Loan" value={helper.nf(activeLoan.loan_amount)} />
                        <LoanRow label="Total Loan" value={helper.nf(activeLoan.total_due)} />
                        <LoanRow label="Paid" value={helper.nf(activeLoan.paid)} />
                        <LoanRow label="Balance" value={helper.nf(activeLoan.balance)} />
                        <LoanRow label="Due Today" value={helper.nf(dueTodayBal)} />
                        <LoanRow label="Defaulted Installment" value={helper.nf(defaultedInstallment)} />
                        <LoanRow label="Penalty Charged" value={helper.nf(activeLoan.penalty_charged)} />

                        <div className="mt-4 space-y-2 text-sm">
                          <button className="flex items-center gap-2 text-primary hover:underline">
                            <Info className="h-4 w-4" />
                            Simplified Loan Info
                          </button>

                          {activeLoan.id ? (
                            <Link
                              to={`/customer/loan/${activeLoan.id}`}
                              className="flex items-center gap-2 text-primary hover:underline"
                            >
                              <List className="h-4 w-4" />
                              View All Loan Info
                            </Link>
                          ) : (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <AlertTriangle className="h-4 w-4" />
                              Loan id missing (backend)
                            </div>
                          )}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-muted-foreground">No active loan information.</div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
            <ConfirmDialog
              open={confirmOpen}
              onOpenChange={(v) => !confirmLoading && setConfirmOpen(v)}
              loading={confirmLoading}
              title="Edit customer profile?"
              description={
                c
                  ? `This will edit a customer record for ${c.fname ?? ""} ${c.sname ?? ""} and open the Customer Wizard.`
                  : "This will create a customer record and open the Customer Wizard."
              }
              confirmText="Edit & Open Wizard"
              cancelText="Cancel"
              onConfirm={convertLeadConfirmed}
            />
          </div>

          {/* Action bar */}
          {!isLead ? (
            <>
              <Card className="shadow-card">
                <CardContent className="p-3">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button className="gap-2">
                            <Pencil className="h-4 w-4" />
                            Account Actions
                            <ChevronDown className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start" className="w-72">
                          {canAddAltPhoneNumber && (
                            <>
                              <DropdownMenuItem onClick={() => openAction("add-alt-phone")}>
                                <Phone className="h-4 w-4 mr-2" />
                                Add Alt Number
                              </DropdownMenuItem>

                              <DropdownMenuItem onClick={() => openAction("add-mpesa-alt")}>
                                <Smartphone className="h-4 w-4 mr-2" />
                                Add Mpesa Payment Numbers
                              </DropdownMenuItem>
                            </>
                          )}
                          {/* <DropdownMenuItem onClick={() => onAccountAction("Make Payment")}>
                            <HandCoins className="h-4 w-4 mr-2" />
                            Make Payment
                          </DropdownMenuItem> */}

                          <DropdownMenuSeparator />
                          {canEditCustomer && (
                            <>
                              <DropdownMenuItem onClick={openConvertConfirm}
                                disabled={convertLoading}>
                                <NotebookPen className="h-4 w-4 mr-2" />
                                Edit Customer
                              </DropdownMenuItem>
                            </>
                          )}
                          {canAddCustomerNote && (
                            <>
                              <DropdownMenuItem onClick={() => openAction("customer-notes")}>
                                <FileText className="h-4 w-4 mr-2" />
                                Customer Notes
                              </DropdownMenuItem>
                            </>
                          )}

                          {canAddPromiseToPay && (
                            <>
                              <DropdownMenuItem onClick={() => openAction("promise-to-pay")}>
                                <ShieldAlert className="h-4 w-4 mr-2" />
                                Promise to Pay
                              </DropdownMenuItem>
                            </>)}
                          {canIncreaseCreditLimit && (
                            <>
                              <DropdownMenuItem onClick={() => openAction("increase-credit-limit")}>
                                <CircleDollarSign className="h-4 w-4 mr-2" />
                                Increase Credit Limit
                              </DropdownMenuItem>
                            </>
                          )}


                          <DropdownMenuSeparator />
                          {activeLoan ? (
                            <>
                              <DropdownMenuItem onClick={() => 2}>
                                <Receipt className="h-4 w-4 mr-2" />
                                Loan Details
                              </DropdownMenuItem>
                              {canIssueLoanCreditDebit && (
                                <>
                                  <DropdownMenuItem onClick={() => 2}>
                                    <BadgePercent className="h-4 w-4 mr-2" />
                                    Issue Credits
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => 2}>
                                    <BadgePercent className="h-4 w-4 mr-2" />
                                    Issue Debits
                                  </DropdownMenuItem>
                                </>
                              )}
                              {/* <DropdownMenuItem onClick={() => 2}>
                                <Trash2 className="h-4 w-4 mr-2" />
                                Write Off Loan
                              </DropdownMenuItem> */}

                            </>
                          ) : (
                            ""
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                     {(canApplyLoanForCustomer && !activeLoan) && (
                        <>
                          <Button variant="secondary" onClick={() => openAction("apply-loan")} className="gap-2">
                            <Pen className="h-4 w-4" />
                            Apply Loan
                          </Button>
                        </>
                      )}
                      {canCanChangeStatus && (
                        <>
                          <Button variant="secondary" onClick={() => openAction("change-status")} className="gap-2">
                            <FileText className="h-4 w-4" />
                            Change Customer Status
                          </Button>
                        </>
                      )}
                      {canEditCustomerPrimaryNumber && (
                        <>
                          <Button variant="secondary" onClick={() => openAction("change-phone")} className="gap-2">
                            <Phone className="h-4 w-4" />
                            Change Number
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  {action ? (
                    <ActionDialog
                      open={!!activeAction}
                      onOpenChange={(o) => !o && closeAction()}
                      title={action.title}
                      endpoint={action.endpoint(String(c?.id ?? ""))}     // ✅ FIX
                      submitLabel={action.submitLabel}
                      defaultValues={action.defaultValues(c)} // ✅ your customer object
                      fields={action.fields}
                      // ✅ called when backend returns success
                      onSuccess={(res) => {
                        console.log("Action result:", res);

                        // refresh customer data
                        // loadCustomer?.();

                        // close dialog
                        closeAction();
                      }}

                      // ✅ optional error handler
                      onError={(res) => {
                        console.error("Action failed:", res);
                      }}

                    />
                  ) : null}
                </CardContent>
              </Card>
            </>
          ) : null}

          {/* BIG SECTION TABS (these will become separate pages/components) */}
          {!isLead ? (
            <Card className="shadow-card">
              <CardContent className="p-4 space-y-4">
                {/* Tabs header */}

                {/* Responsive tabs — mobile: wrapped rows, desktop: single row */}
                <div className="border-b pb-2">
                  <div
                    className={cn(
                      "flex flex-wrap items-center gap-2", // 🔥 allows multiple rows
                      "text-xs sm:text-sm font-semibold"
                    )}
                  >
                    <TabBtn active={activeTab === "loan_status"} onClick={() => openTab("loan_status")}>
                      LOAN STATUS
                    </TabBtn>

                    <TabBtn active={activeTab === "schedule"} onClick={() => openTab("schedule")}>
                      SCHEDULE
                    </TabBtn>

                    <TabBtn active={activeTab === "attachments"} onClick={() => openTab("attachments")}>
                      ATTACHMENTS
                    </TabBtn>

                    <TabBtn active={activeTab === "assets"} onClick={() => openTab("assets")}>
                      ASSETS
                    </TabBtn>

                    <TabBtn active={activeTab === "promise_to_pay"} onClick={() => openTab("promise_to_pay")}>
                      PROMISE TO PAY
                    </TabBtn>

                    <TabBtn active={activeTab === "referee"} onClick={() => openTab("referee")}>
                      REFEREE
                    </TabBtn>

                    <TabBtn active={activeTab === "business"} onClick={() => openTab("business")}>
                      BUSINESS
                    </TabBtn>

                    <TabBtn active={activeTab === "other_details"} onClick={() => openTab("other_details")}>
                      OTHER DETAILS
                    </TabBtn>

                    <TabBtn active={activeTab === "location"} onClick={() => openTab("location")}>
                      LOCATION
                    </TabBtn>
                  </div>
                </div>



                {/* TAB CONTENT (placeholders ready to attach components) */}
                <div className="pt-1">
                  {activeTab === "loan_status" && (
                    <div className="space-y-3">
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                        <KpiCircle value={Number(activeLoan?.loan_days ?? 0)} label="Current loan days Running" />
                        <KpiCircle value={Number(c?.defaulted_times ?? 0)} label="Number of times defaulted" />
                        <KpiCircle value={Number(activeLoan?.duplum_locked ?? 0)} label="Duplum Locked" />
                      </div>
                    </div>
                  )}

                  {activeTab === "schedule" && (
                    <div className="space-y-3">
                      {/* ✅ ATTACH SCHEDULE TAB HERE */}
                      <ActiveLoanScheduleTab customerId={String(c.id)} />

                    </div>
                  )}

                  {activeTab === "attachments" && (
                    <div className="space-y-3">
                      {/* ✅ ATTACH ATTACHMENTS TAB HERE */}
                      <CustomerAttachmentsTab customerId={String(c.id)} />
                    </div>
                  )}

                  {activeTab === "assets" && (
                    <div className="space-y-3">
                      {/* ✅ ATTACH ASSETS TAB HERE */}
                      <CustomerAssetsTab customerId={String(c.id)} />

                    </div>
                  )}

                  {activeTab === "promise_to_pay" && (
                    <div className="space-y-3">
                      {/* ✅ ATTACH PROMISE TO PAY TAB HERE */}
                      <Promise2PayTab customerId={String(c.id)} />

                    </div>
                  )}

                  {activeTab === "referee" && (
                    <div className="space-y-3">
                      {/* ✅ ATTACH REFEREE TAB HERE */}
                      <CustomerRefereesTab customerId={String(c.id)} />

                    </div>
                  )}

                  {activeTab === "business" && (
                    <div className="space-y-3">
                      {/* ✅ ATTACH BUSINESS TAB HERE */}
                      <CustomerBusinessTab customerId={String(c.id)} />

                    </div>
                  )}

                  {activeTab === "other_details" && (
                    <div className="space-y-3">
                      {/* ✅ ATTACH OTHER DETAILS TAB HERE */}
                      <CustomerOtherDetailsTab customerId={String(c.id)} />

                    </div>
                  )}

                  {activeTab === "location" && (
                    <div className="space-y-3">
                      {/* ✅ ATTACH LOCATION TAB HERE */}
                      <CustomerLocationTab customerId={String(c.id)} />

                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ) : null}

          {!isLead ? (
            <Card className="shadow-card">
              <CardContent className="p-4 space-y-4">
                <CustomerLoans customerId={String(c.id)} />
              </CardContent>
            </Card>
          ) : null}

          {/* Payments section */}
          {!isLead ? (
            <Card className="shadow-card">
              <CardContent className="p-4">
                <Tabs
                  defaultValue="payments"
                  onValueChange={(v) => {
                    // optional: if you want to trigger lazy load inside each tab component,
                    // pass `active` prop to the component OR keep it internal to the component.
                    // Example:
                    // setPaymentsTabActive(v === "payments");
                  }}
                >
                  <div className="overflow-x-auto">
                    <TabsList className="min-w-max">
                      <TabsTrigger value="payments">Payments</TabsTrigger>
                      <TabsTrigger value="charges">Charges</TabsTrigger>

                      <TabsTrigger value="notes">Notes</TabsTrigger>
                    </TabsList>
                  </div>

                  <TabsContent value="payments" className="mt-4 space-y-4">
                    {/* Payments tab */}
                    <ActiveLoanPayments customerId={String(c?.id ?? "")} />
                  </TabsContent>

                  <TabsContent value="charges" className="mt-4 space-y-4">
                    {/* Charges tab */}
                    <ActiveLoanChargesTab customerId={String(c?.id ?? "")} />
                  </TabsContent>



                  <TabsContent value="notes" className="mt-4 space-y-4">
                    {/* Notes tab */}
                    <CustomerNotesTab customerId={String(c?.id ?? "")} />
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : null}
        </motion.div>
      ) : null}
    </div>
  );
}

/** ------------------- TAB PLACEHOLDER (we will replace with real pages) ------------------- */


/** Reusable UI blocks */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return <div className="text-sm font-semibold text-primary italic mb-3">{children}</div>;
}

function InfoRow({
  icon: Icon,
  label,
  value,
  rightClass,
}: {
  icon: any;
  label: string;
  value: string;
  rightClass?: string;
}) {
  return (
    <div className="py-1.5">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary shrink-0" />
        <div className="text-sm font-semibold text-foreground/80">{label}:</div>
        <div className={cn("flex-1 min-w-0", rightClass)}>
          <span className="text-sm font-medium border-b border-dashed border-foreground/40">{value}</span>
        </div>
      </div>
    </div>
  );
}

function LoanRow({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="py-1.5">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm font-semibold text-foreground/80">{label}:</div>
        <div className="text-sm font-semibold border-b border-dashed border-foreground/40">{value}</div>
      </div>
    </div>
  );
}

function KpiCircle({ value, label }: { value: number; label: string }) {
  return (
    <div className="rounded-xl border bg-background p-4 flex items-center justify-center">
      <div className="text-center space-y-2">
        <div className="mx-auto h-14 w-14 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xl font-bold">
          {Number.isFinite(value) ? value : 0}
        </div>
        <div className="text-sm font-medium text-foreground/80">{label}</div>
      </div>
    </div>
  );
}

function TabBtn({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-2 rounded-lg transition-all",
        "text-xs sm:text-sm font-semibold",
        "whitespace-nowrap", // keeps label clean
        active
          ? "bg-primary text-primary-foreground shadow-sm"
          : "bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80"
      )}
    >
      {children}
    </button>
  );
}